import { useCallback } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { useQueries, type UseQueryResult } from "@tanstack/react-query"

import type { TransportOperationName } from "@/types_api/backend/transport/operations"

import type { TransportQueriesResult } from "../contracts"
import { TransportRequestError } from "../utils/transportClient"
import { executeTransportOperation } from "./transportOperationRuntime"
import { getTransportQueryKey } from "./transportQueryKeys"
import type { TransportRequest, TransportResponse } from "./transportQueryTypes"
import { useTransportOperationContext } from "./useTransportOperationContext"

function isUnavailable(error: unknown): boolean {
  return (
    error instanceof TransportRequestError &&
    [401, 403, 404, 410].includes(error.status)
  )
}

/** Resolve a bounded set of referenced resources using individually scoped query keys. */
export function useTransportQueries<Name extends TransportOperationName>({
  operation,
  requests,
  enabled = true,
  ignoreNotFound = false,
  revalidateOnMount = false,
}: {
  operation: Name
  requests: TransportRequest<Name>[]
  enabled?: boolean
  ignoreNotFound?: boolean
  revalidateOnMount?: boolean | "stale"
}): TransportQueriesResult<Name> {
  const { getClient, isReady, queryOrganizationSlug, queryWorkspaceId } =
    useTransportOperationContext(operation)

  const combine = useCallback(
    (results: UseQueryResult<TransportResponse<Name>>[]) => {
      // A transient refresh failure keeps cached definitions; missing or denied resources discard them.
      const responses = results.flatMap((result, index) =>
        result.data &&
        (revalidateOnMount !== true || result.isFetchedAfterMount) &&
        !isUnavailable(result.error)
          ? [{ request: requests[index], data: result.data }]
          : [],
      )
      return {
        responses,
        failedRequests: results.flatMap((result, index) =>
          result.error ? [requests[index]] : [],
        ),
        unavailableRequests: results.flatMap((result, index) =>
          isUnavailable(result.error) ? [requests[index]] : [],
        ),
        data: responses.map(({ data }) => data),
        isLoading:
          enabled &&
          results.some(
            (result) =>
              result.isPending ||
              (revalidateOnMount === true && !result.isFetchedAfterMount),
          ),
        refetch: () => Promise.all(results.map((result) => result.refetch())),
        error:
          results.find(
            (result) =>
              result.error &&
              (!ignoreNotFound ||
                !(result.error instanceof TransportRequestError) ||
                result.error.status !== 404),
          )?.error ?? null,
      }
    },
    [enabled, ignoreNotFound, requests, revalidateOnMount],
  )

  return useQueries({
    combine,
    queries: requests.map((request) => ({
      ...CACHE_PROFILES.lookup,
      ...(revalidateOnMount && {
        ...(revalidateOnMount === true && { staleTime: 0 }),
        refetchOnMount: revalidateOnMount === true ? ("always" as const) : true,
      }),
      enabled: enabled && isReady,
      queryKey: getTransportQueryKey(
        operation,
        request,
        queryOrganizationSlug,
        queryWorkspaceId,
      ),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        executeTransportOperation(getClient(), operation, request, { signal }),
    })),
  })
}
